/**
 * The **Arena class registry** (PRD #446, Slice 0, ADR-0236) - the seam the run
 * handler validates against and the sim/UI read the class list from.
 *
 * One entry today ({@link FIRE_MAGE_CLASS}); the archer/swordsman/bard register
 * here as their slices land, and the portal picker, the `StartArenaRun.classId`
 * validation, and the per-class draft pools all follow from the registry with
 * no new wiring (the storm-body registry pattern - register the def and the
 * gates derive themselves).
 */

import type { ArenaClassId, ArenaClassDef } from './types';
import { FIRE_MAGE_CLASS } from './fireMage';
import { ARCHER_CLASS } from './archer';

export type { ArenaClassId, ArenaClassDef, ArenaClassSkill, ArenaClassInnate, ArenaBalanceFloor } from './types';
export { FIRE_MAGE_CLASS } from './fireMage';
export { ARCHER_CLASS } from './archer';

/** The class an absent/unspecified `StartArenaRun.classId` resolves to - the
 * original identity (PRD #446: "the fire mage untouched by default"). */
export const DEFAULT_ARENA_CLASS_ID: ArenaClassId = 'fire-mage';

/**
 * Every Arena class def, keyed by id - the single source of truth for the
 * playable set. `startArenaRun` and the server's entry validation both read
 * it; the draft pool reads each run's captured def's own modifier/keystone
 * lines through it.
 */
export const ARENA_CLASSES: Record<ArenaClassId, ArenaClassDef> = {
  'fire-mage': FIRE_MAGE_CLASS,
  archer: ARCHER_CLASS,
};

/** Every class id, in registration order (the portal picker's list order). */
export const ARENA_CLASS_IDS: readonly ArenaClassId[] = Object.keys(ARENA_CLASSES) as ArenaClassId[];

/** Whether `id` names a registered class - the `StartArenaRun.classId` guard
 * (an unknown id refuses the run in fiction, the crowded-gate pattern). */
export function isArenaClassId(id: unknown): id is ArenaClassId {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(ARENA_CLASSES, id);
}

/**
 * Resolve a class def by id, falling back to the default for `undefined`
 * (an absent wire `classId` = the fire mage). An **unknown** id returns
 * `undefined` so the caller can refuse in fiction - it is never silently
 * defaulted (a forged/stale id is a deliberate refusal, not a fire-mage run).
 */
export function getArenaClass(id: ArenaClassId | undefined): ArenaClassDef | undefined {
  if (id === undefined) return ARENA_CLASSES[DEFAULT_ARENA_CLASS_ID];
  return isArenaClassId(id) ? ARENA_CLASSES[id] : undefined;
}
